import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  IconButton,
  Collapse,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Headeer from "../../components/Headeer";
import PatientService from "../../services/doctorServices/patientService";
import { tokens } from "../../theme";
import { useTheme } from "@emotion/react";
import { translateBackendError } from "../../utils/errorTranslator";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
} from "recharts";

// Aggregate invitations by status for a list of invitations
function countByStatus(invitations) {
  const list = Array.isArray(invitations) ? invitations : [];
  return {
    total: list.length,
    pending: list.filter((i) => i.status === "pending").length,
    completed: list.filter((i) => i.status === "answered").length,
    expired: list.filter((i) => i.status === "expired" || i.status === "revoked").length,
  };
}

export default function PatientStatistics() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation(["patients", "common"]);
  const { id } = useParams();
  const patientService = new PatientService();

  const [patientInfo, setPatientInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedFileId, setExpandedFileId] = useState(null);
  const [formIndex, setFormIndex] = useState(0);

  useEffect(() => {
    setFormIndex(0);
  }, [expandedFileId]);

  const fetchPatientInfo = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const response = await patientService.getPatientInfo(id);
      setPatientInfo(response);
    } catch (err) {
      if (err.response?.status === 403) {
        return;
      }
      const errorResponse = err.response?.data;
      const errorMessage = errorResponse
        ? translateBackendError(errorResponse)
        : t("patients:errorFetchingPatientInfo");
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    fetchPatientInfo();
  }, [fetchPatientInfo]);

  const patientName = patientInfo?.patient
    ? [patientInfo.patient.firstName, patientInfo.patient.lastName].filter(Boolean).join(" ") ||
      t("patients:patientProfile")
    : t("patients:patientProfile");

  // Group forms by file and compute file-level stats (support file_id and fileId)
  const filesWithStats = useMemo(() => {
    const patient = patientInfo?.patient;
    if (!patient?.Files?.length) {
      return (patient?.Files || []).map((f) => ({
        file: f,
        forms: [],
        totalForms: 0,
        totalSent: 0,
        pending: 0,
        completed: 0,
        expired: 0,
      }));
    }
    const forms = patient?.Forms || [];
    return patient.Files.map((file) => {
      const formsInFile = forms.filter(
        (f) => (f.file_id || f.fileId) === file.id
      );
      let totalSent = 0;
      let pending = 0;
      let completed = 0;
      let expired = 0;
      formsInFile.forEach((form) => {
        const invs = form.invitations || [];
        totalSent += invs.length;
        invs.forEach((i) => {
          if (i.status === "pending") pending++;
          else if (i.status === "answered") completed++;
          else if (i.status === "expired" || i.status === "revoked") expired++;
        });
      });
      return {
        file,
        forms: formsInFile,
        totalForms: formsInFile.length,
        totalSent,
        pending,
        completed,
        expired,
      };
    });
  }, [patientInfo?.patient]);

  const toggleFileExpand = (fileId) => {
    setExpandedFileId((prev) => (prev === fileId ? null : fileId));
  };

  // Build chart data for a form: formAnswers sorted by answered_at asc -> [{ submission: "1", point: 10, label: "Submission #1" }, ...]
  const getFormChartData = (form) => {
    const answers = form.formAnswers || [];
    const sorted = [...answers].sort(
      (a, b) => new Date(a.answered_at || 0) - new Date(b.answered_at || 0)
    );
    return sorted.map((a, idx) => ({
      submission: String(idx + 1),
      point: Number(a.point) || 0,
      label: t("patients:submissionNumber", { number: idx + 1 }),
    }));
  };

  const statCard = (label, value, bgColor) => (
    <Card
      sx={{
        background: bgColor,
        borderRadius: 2,
        border: `1px solid ${colors.grey[700]}40`,
        minWidth: 100,
      }}
    >
      <CardContent sx={{ py: 1.5, px: 2, "&:last-child": { pb: 1.5 } }}>
        <Typography variant="caption" sx={{ color: colors.grey[300], fontWeight: 600, display: "block" }}>
          {label}
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 700, color: colors.grey[100] }}>
          {value}
        </Typography>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Headeer
        title={t("patients:statisticsTitle")}
        subtitle={t("patients:statisticsSubtitle", { name: patientName })}
      />

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress sx={{ color: colors.blueAccent[500] }} />
        </Box>
      )}

      {error && (
        <Typography color="error" sx={{ py: 2 }}>
          {error}
        </Typography>
      )}

      {!loading && !error && patientInfo && (
        <Box sx={{ mt: 3 }}>
          {filesWithStats.length === 0 ? (
            <Typography sx={{ color: colors.grey[400] }}>{t("patients:noFiles")}</Typography>
          ) : (
            filesWithStats.map(({ file, forms, totalForms, totalSent, pending, completed, expired }) => (
              <Card
                key={file.id}
                sx={{
                  mb: 3,
                  borderRadius: 3,
                  background: `linear-gradient(135deg, ${colors.primary[600]} 0%, ${colors.primary[700]} 100%)`,
                  border: `1px solid ${colors.grey[700]}40`,
                  overflow: "hidden",
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      gap: 2,
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 700, color: colors.grey[100] }}>
                      {file.name}
                    </Typography>
                    <IconButton
                      onClick={() => toggleFileExpand(file.id)}
                      sx={{
                        color: colors.grey[100],
                        backgroundColor: `${colors.blueAccent[700]}40`,
                        "&:hover": { backgroundColor: `${colors.blueAccent[600]}60` },
                      }}
                    >
                      {expandedFileId === file.id ? (
                        <ExpandLessIcon />
                      ) : (
                        <ExpandMoreIcon />
                      )}
                    </IconButton>
                  </Box>
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={6} sm>
                      {statCard(
                        t("patients:totalForms"),
                        totalForms,
                        `linear-gradient(135deg, ${colors.primary[500]} 0%, ${colors.primary[600]} 100%)`
                      )}
                    </Grid>
                    <Grid item xs={6} sm>
                      {statCard(
                        t("patients:totalSent"),
                        totalSent,
                        `linear-gradient(135deg, ${colors.blueAccent[700]} 0%, ${colors.blueAccent[800]} 100%)`
                      )}
                    </Grid>
                    <Grid item xs={6} sm>
                      {statCard(
                        t("patients:pending"),
                        pending,
                        `linear-gradient(135deg, ${colors.greenAccent[700]} 0%, ${colors.greenAccent[800]} 100%)`
                      )}
                    </Grid>
                    <Grid item xs={6} sm>
                      {statCard(
                        t("patients:completed"),
                        completed,
                        `linear-gradient(135deg, ${colors.blueAccent[600]} 0%, ${colors.blueAccent[700]} 100%)`
                      )}
                    </Grid>
                    <Grid item xs={6} sm>
                      {statCard(
                        t("patients:expired"),
                        expired,
                        `linear-gradient(135deg, ${colors.grey[700]} 0%, ${colors.grey[800]} 100%)`
                      )}
                    </Grid>
                  </Grid>

                  <Collapse in={expandedFileId === file.id}>
                    <Box sx={{ mt: 3, pt: 3, borderTop: `1px solid ${colors.grey[700]}40` }}>
                      {forms.length === 0 ? (
                        <Typography sx={{ color: colors.grey[400] }}>
                          {t("patients:noFormsInFile")}
                        </Typography>
                      ) : (
                        <Box>
                          {(() => {
                            const idx = Math.min(Math.max(0, formIndex), forms.length - 1);
                            const form = forms[idx];
                            if (!form) return null;
                            const invStats = countByStatus(form.invitations);
                            const chartData = getFormChartData(form);
                            return (
                              <Card
                                key={form.id}
                                sx={{
                                  borderRadius: 2,
                                  background: `${colors.primary[700]}80`,
                                  border: `1px solid ${colors.grey[700]}40`,
                                }}
                              >
                                <CardContent sx={{ p: 3 }}>
                                  <Typography
                                    variant="subtitle1"
                                    sx={{ fontWeight: 700, color: colors.grey[100], mb: 2 }}
                                  >
                                    {form.name}
                                  </Typography>
                                <Grid container spacing={2} sx={{ mb: 3 }}>
                                  <Grid item xs={4}>
                                    {statCard(
                                      t("patients:formTotalSent"),
                                      invStats.total,
                                      `${colors.blueAccent[800]}80`
                                    )}
                                  </Grid>
                                  <Grid item xs={4}>
                                    {statCard(
                                      t("patients:formPending"),
                                      invStats.pending,
                                      `${colors.greenAccent[800]}80`
                                    )}
                                  </Grid>
                                  <Grid item xs={4}>
                                    {statCard(
                                      t("patients:formCompleted"),
                                      invStats.completed,
                                      `${colors.blueAccent[700]}80`
                                    )}
                                  </Grid>
                                </Grid>
                                {chartData.length > 0 && (
                                  <Box sx={{ mt: 2 }}>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: colors.grey[300],
                                        fontWeight: 600,
                                        display: "block",
                                        mb: 1,
                                      }}
                                    >
                                      {t("patients:scoreBySubmission")}
                                    </Typography>
                                    <ResponsiveContainer width="100%" height={220}>
                                      <BarChart
                                        data={chartData}
                                        margin={{ top: 10, right: 20, left: 10, bottom: 5 }}
                                      >
                                        <CartesianGrid strokeDasharray="3 3" stroke={colors.grey[700]} />
                                        <XAxis
                                          dataKey="submission"
                                          tick={{ fill: colors.grey[300], fontSize: 12 }}
                                          tickFormatter={(v, i) =>
                                            chartData[i]?.label || `#${v}`
                                          }
                                        />
                                        <YAxis
                                          tick={{ fill: colors.grey[300], fontSize: 12 }}
                                          domain={[0, "auto"]}
                                        />
                                        <Tooltip
                                          contentStyle={{
                                            background: colors.primary[800],
                                            border: `1px solid ${colors.grey[700]}`,
                                            borderRadius: 8,
                                          }}
                                          labelStyle={{ color: colors.grey[100] }}
                                          formatter={(value) => [value, t("patients:totalScore")]}
                                          labelFormatter={(_, payload) =>
                                            payload?.[0]?.payload?.label || ""
                                          }
                                        />
                                        <Bar dataKey="point" fill={colors.blueAccent[500]} radius={[4, 4, 0, 0]}>
                                          {chartData.map((_, index) => (
                                            <Cell
                                              key={index}
                                              fill={
                                                index % 2 === 0
                                                  ? colors.blueAccent[500]
                                                  : colors.greenAccent[500]
                                              }
                                            />
                                          ))}
                                        </Bar>
                                      </BarChart>
                                    </ResponsiveContainer>
                                  </Box>
                                )}
                                </CardContent>
                              </Card>
                            );
                          })()}
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 2,
                              mt: 2,
                            }}
                          >
                            <IconButton
                              onClick={() => setFormIndex((i) => Math.max(0, i - 1))}
                              disabled={forms.length <= 1 || formIndex === 0}
                              sx={{
                                color: colors.grey[100],
                                backgroundColor: `${colors.blueAccent[700]}40`,
                                "&:hover": { backgroundColor: `${colors.blueAccent[600]}60` },
                                "&.Mui-disabled": { color: colors.grey[600] },
                              }}
                            >
                              <ChevronLeftIcon />
                            </IconButton>
                            <Typography variant="body2" sx={{ color: colors.grey[300], fontWeight: 600 }}>
                              {formIndex + 1} / {forms.length}
                            </Typography>
                            <IconButton
                              onClick={() => setFormIndex((i) => Math.min(forms.length - 1, i + 1))}
                              disabled={forms.length <= 1 || formIndex >= forms.length - 1}
                              sx={{
                                color: colors.grey[100],
                                backgroundColor: `${colors.blueAccent[700]}40`,
                                "&:hover": { backgroundColor: `${colors.blueAccent[600]}60` },
                                "&.Mui-disabled": { color: colors.grey[600] },
                              }}
                            >
                              <ChevronRightIcon />
                            </IconButton>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </Collapse>
                </CardContent>
              </Card>
            ))
          )}
        </Box>
      )}
    </Box>
  );
}
